import { Controller } from "@hotwired/stimulus"

// A one-shot side-effect controller. The Turbo Stream response for a
// checkpoint advance appends one of these into #reader-sync-slot alongside
// the plain DOM insert/replace streams that carry the actual content
// change. connect() runs once per element and handles the three things a
// DOM mutation can't do on its own:
//
//   - moves real focus to the newly-revealed section (or the completion
//     heading, on the final checkpoint) — Turbo Streams don't touch focus,
//     and without this a keyboard/screen-reader user's place would be lost;
//   - writes the "Section N complete. Section N+1 of M is now available."
//     announcement into the existing sr-only live region;
//   - animates the progress bar to its new width by mutating the fill
//     element that's already on the page (reader_position_controller.js's
//     target), rather than replacing it — a replaced element has no prior
//     width to transition from, so this is what makes the existing
//     `transition-[width]` rule on that element actually animate here.
//
// It then removes itself: it exists only to run its side effects once, not
// as a persistent widget, so #reader-sync-slot doesn't grow across repeated
// checkpoint submissions.
export default class extends Controller {
  static values = { percentage: Number, focusSelector: String, announce: String }

  connect() {
    this.syncProgress()
    this.moveFocus()
    this.announce()
    this.element.remove()
  }

  syncProgress() {
    if (!this.hasPercentageValue) return

    const fill = document.querySelector('[data-reader-position-target="progressFill"]')
    const bar = document.querySelector('[data-reader-position-target="progressBar"]')
    const label = document.querySelector('[data-reader-position-target="progressLabel"]')

    if (fill) fill.style.width = `${this.percentageValue}%`
    if (bar) bar.setAttribute("aria-valuenow", this.percentageValue)
    if (label) label.textContent = `${this.percentageValue}%`
  }

  moveFocus() {
    if (!this.hasFocusSelectorValue || !this.focusSelectorValue) return

    const target = document.querySelector(this.focusSelectorValue)
    if (!target) return

    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1")
    target.focus({ preventScroll: true })

    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false
    target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" })
  }

  announce() {
    if (!this.hasAnnounceValue || !this.announceValue) return

    const live = document.getElementById("reader-live-status")
    if (live) live.textContent = this.announceValue
  }
}
